import React, { useState, useMemo, useEffect } from "react";
import useStyles from "./style"
import { Box, Typography, Popover } from "@material-ui/core"
import { InfoOutlined } from "@material-ui/icons"
import { vppService } from '../../../Services/VppService/index'
import { Loader } from "../../../Components";
import { systemsService } from "../../../Services/SystemsService/SystemsService"
import { connect } from "react-redux";
import NetCapacityChart from './graph'
import { toDate } from "date-fns"
import { ERROR, SUCCESS } from '../../../utils/messageTypes'
import DisplayMessage from '../../../Components/DisplayMessage'



const CustomGraphs = (props) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const [forecastDataList, setforecastDataList] = useState({})
  const [loading, setLoading] = useState(false)
  const [historicData, setHistoricData] = useState([])
  const [forcastNetCapacityList, setForcastNetCapacityList] = useState([])
  const [completeData, setCompleteData] = useState([])
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  function getUtcDate(dayOffset) {
    const now = new Date();
    const utcDate = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + dayOffset
    ));

    return utcDate;
  }

  function formatUtcDDMMYYYY(utcDate) {
    const day = utcDate.getUTCDate();
    const month = utcDate.getUTCMonth() + 1;
    const year = utcDate.getUTCFullYear();

    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(day)}-${pad(month)}-${year}`;
  }

  const yesterdayUtc = getUtcDate(-1)
  const yesterdayUtcString = formatUtcDDMMYYYY(yesterdayUtc)

  const todayUtc = getUtcDate(0)
  const todayUtcString = formatUtcDDMMYYYY(todayUtc)


  const getAllForcastData = () => {
    setLoading(true)
    vppService.getAllForcastData(props.site_ids, props.duration, props.granularity)
    .then((response) => {
      setforecastDataList(response)
      calculateNetCapacityForcast(response)
      setLoading(false)
    }).catch((error) => {
      setforecastDataList([])
      setOpen(true)
      setMessageType(ERROR)
      setMessage("No forecast data found")
      setLoading(false)
    })
  }

  const aggregateSiteArrays = (allSiteArrays) => {
    if (!allSiteArrays || allSiteArrays.length === 0) return [];
    const aggregated = Array.from({ length: 96 }, () => ({
      net_grid_export: 0,
      available_battery_capacity_kwh: 0,
      grid_export: 0
    }));

    allSiteArrays.forEach(siteArr => {
      for (let i = 0; i < 96; i++) {
        aggregated[i].net_grid_export += (siteArr[i].net_grid_export || 0)
        aggregated[i].available_battery_capacity_kwh += (siteArr[i].available_battery_capacity_kwh || 0)
        aggregated[i].grid_export += (siteArr[i].grid_export || 0)
      }
    });
    return aggregated;
  };

  useEffect(() => {
    if (!props.site_ids || props.site_ids.length === 0) return;

    const fetchAllHistoricData = async () => {
      setLoading(true);
      try {
          const responses = await Promise.all(
            props.site_ids.map(siteId =>
              systemsService.getSystemsDailyGraph(
                props.accountId,
                siteId,
                true,
                yesterdayUtcString,
                todayUtcString
              )
            )
          )
        const allSiteArrays = await Promise.all(
          responses.map(async (response) => {
            const nameplate_energy = await getNamplateEnergy(response.system_id)
            return calculateHistoricData(response, nameplate_energy)
          })
        )

        const aggregated = aggregateSiteArrays(allSiteArrays)
        setHistoricData(aggregated);
      } catch (err) {
        setOpen(true)
        setMessageType(ERROR)
        setMessage("Failed to fetch historic data for sites", err)
      } finally {
        setLoading(false);
      }
    }

  fetchAllHistoricData()
}, [props.site_ids])

const getNamplateEnergy = async (system_id) => {
  setLoading(true)
  try {
    const response = await systemsService.getSiteEnrollmentInfo(system_id)
    return response.nameplate_energy
  } catch (error) {
    setOpen(true)
    setMessageType(ERROR)
    setMessage(error.data.error.additionalInfo)
    throw error
  } finally {
    setLoading(false)
  }
}

useEffect(() => {
  if(props.site_ids.length > 0){
    getAllForcastData()
  }
}, [props.site_ids, props.duration, props.granularity])


useEffect(() => {
  if(historicData.length > 0 && forcastNetCapacityList.length > 0){
    const combinedData = [...historicData, ...forcastNetCapacityList]
    const graphData = addTimeToData(combinedData)
    setCompleteData(graphData)
  }
}, [historicData, forcastNetCapacityList])

function addTimeToData(data) {
  let now = new Date();
  const minutes = now.getUTCMinutes();
  const remainder = minutes % 15;
  const minutesToAdd = remainder === 0 ? 15 : (15 - remainder);

  now = new Date(now.getTime() + minutesToAdd * 60 * 1000);
  const startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  return data.map((value, index) => {
    const pointTime = new Date(startTime.getTime() + (index * 15 * 60 * 1000));
    const hh = pointTime.getUTCHours().toString().padStart(2, "0");
    const mm = pointTime.getUTCMinutes().toString().padStart(2, "0");
    return {
      value,
      time: `${hh}:${mm}`
    };
  });
}


  function getHoursAndMinutesFromEpoch(epochSeconds) {
    const epochMilliseconds = epochSeconds * 1000
    const dateObject = new Date(epochMilliseconds)

    const hours = dateObject.getUTCHours()
    const minutes = dateObject.getUTCMinutes()

    const pad = (num) => String(num).padStart(2, '0')
    const formattedTime = `${pad(hours)}:${pad(minutes)}`
    return formattedTime
  }

  const getCurrentUTCTime = () => {
    const now = new Date()
    const hours = now.getUTCHours()
    const minutes = now.getUTCMinutes()
    const pad = (num) => String(num).padStart(2, '0')
    return `${pad(hours)}:${pad(minutes)}`
  }

function timeToMinutes(timeStr) {
  const parts = timeStr.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  return (hours * 60) + minutes;
}

const getIntervals = (startTime, endTime) => {    
  const INTERVAL_MINUTES = 15
  const startMinutes = timeToMinutes(startTime)
  const endMinutes = timeToMinutes(endTime)

  const durationMinutes = endMinutes - startMinutes
  if (durationMinutes < 0) {
    return 0
  }

  const numberOfIntervals = Math.floor(durationMinutes / INTERVAL_MINUTES)
  return numberOfIntervals
}

const calculateHistoricData = (response, namplate_energy) => {
  let start_time = response.stats[1] ? getHoursAndMinutesFromEpoch(response.stats[1].start_time) : getCurrentUTCTime()  
  let today_end_time = getCurrentUTCTime()

  const numberOfIntervals = getIntervals(start_time, today_end_time)  
  let netcapacity = []
 
  for(let i = numberOfIntervals; i < 96; i++){
    var netGridCapacity = response.stats[0]?.export[i]/1000 - response.stats[0]?.import[i]/1000
    var netAvailableEnergy = namplate_energy*(response.stats[0]?.soc[i])/100 
    var gridExport = response.stats[0]?.export[i]/1000
    netcapacity.push({net_grid_export: netGridCapacity, available_battery_capacity_kwh: netAvailableEnergy, grid_export: gridExport})
  }

  for(let i = 0; i < numberOfIntervals && netcapacity.length < 96; i++){
    var netGridCapacity = response.stats[1]?.export[i]/1000 - response.stats[1]?.import[i]/1000 
    var netAvailableEnergy = namplate_energy*(response.stats[1]?.soc[i])/100
    var gridExport = response.stats[1]?.export[i]/1000
    netcapacity.push({net_grid_export: netGridCapacity, available_battery_capacity_kwh: netAvailableEnergy, grid_export: gridExport})
  }
  return netcapacity;
}

const calculateNetCapacityForcast = (response) => {
  const forcastNetCapacity = [];

  for (let i = 0; i < 96; i++) {
    let netGridCapacity = response.intervals[i].grid_export_forecast_wh - response.intervals[i].grid_import_forecast_wh
    let netAvailableEnergy = response.intervals[i].available_battery_capacity_wh + response.intervals[i].battery_charging_forecast_wh - response.intervals[i].battery_discharging_forecast_wh
    let gridExport = response.intervals[i].grid_export_forecast_wh

    forcastNetCapacity.push({net_grid_export: netGridCapacity/1000, available_battery_capacity_kwh: netAvailableEnergy/1000, grid_export: gridExport/1000});
  }
  setForcastNetCapacityList(forcastNetCapacity)
}

const handleClose = (e) => {
  setAnchorEl(null)
}

const handleInfoClick = (event) => {
  setAnchorEl(event.currentTarget)
}

  const openPopover = Boolean(anchorEl)
  const id = openPopover ? 'simple-popover' : null

  return (
    <Box className={classes.container}>
      <Box className={classes.graphContainer}>
        <Box className={classes.titleWrapper}>
          <Typography className={classes.graphTitle}> Grid Export and Available Battery Capacity(kWh) </Typography>
          <InfoOutlined className={classes.infoIcon} onClick={handleInfoClick}/>
          <Popover
            id={id}
            open={openPopover}
            anchorEl={anchorEl}
            onClick={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            PaperProps={{
              className: classes.infoPopoverPaper,
            }}
          >
            <Box className={classes.infoPopover} p={1.5}>
              {"Net Grid Export is Grid Export - Grid Import, Available Battery Capacity is Available battery capacity + Battery charge - Battery discharge"}
            </Box>
          </Popover>
        </Box>
        { forecastDataList.intervals && forecastDataList.intervals.length > 0 && 
          <Box marginLeft='40px' style={{display: 'flex'}}>
            {completeData && completeData.length > 0 && <NetCapacityChart data={completeData}/>}
          </Box> 
          }
        </Box>
      <Loader loading={loading} />
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
    </Box>
  )
}



const mapStateToProps = ({ auth }) => {
  const { accountId } = auth
  return { accountId }
}

export default connect(mapStateToProps, {})(CustomGraphs)